import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createVendor as createVendorFn,
  updateVendor as editVendorService,
  deleteVendor as deleteVendorService,
} from "../services/vendor-service";
import { CreateVendorRequest } from "../models/request/create-vendor-request";
import { ApiError } from "pages/admin-app/shared/models/response/api-errors";
import { CreateVendorResponse } from "../models/response/create-vendor-response";
import { EditVendorResponse } from "../models/response/edit-vendor-response";
import { AxiosError } from "axios";
import { EditVendorRequest } from "../models/request/edit-vendor-request";

interface Props {
  id?: number | string;
  onEditSuccess?: () => void;
  onEditError?: () => void;
  onCreateSuccess?: () => void;
  onCreateError?: () => void;
}

export interface ApiErrorResponse {
  message: string;
  error: string;
  statusCode: number;
}

export default function useMutateVendor(props?: Props) {
  const queryClient = useQueryClient();

  const {
    mutate: editVendor,
    error: editVendorError,
    isPending: editVendorLoading,
    isSuccess: editVendorSuccess,
  } = useMutation<
    EditVendorResponse,
    AxiosError<ApiErrorResponse>,
    EditVendorRequest
  >({
    mutationKey: ["vendor", props?.id, "edit"],
    mutationFn: editVendorService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", props?.id] });
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
      props?.onEditSuccess && props?.onEditSuccess();
    },
    onError: () => {
      props?.onEditError && props?.onEditError();
    },
  });

  const {
    mutate: deleteVendor,
    error: deleteVendorError,
    isPending: deleteVendorLoading,
    reset: deleteVendorReset,
    isSuccess: deleteVendorSuccess,
    isError: deleteVendorIsError,
  } = useMutation({
    mutationKey: ["vendor", props?.id, "delete"],
    mutationFn: deleteVendorService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
    },
  });

  const {
    mutate: createVendor,
    error: createVendorError,
    isPending: createVendorLoading,
    isSuccess: createVendorSuccess,
  } = useMutation<CreateVendorResponse, ApiError, CreateVendorRequest>({
    mutationKey: ["vendor", props?.id, "create"],
    mutationFn: createVendorFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
      props?.onCreateSuccess && props?.onCreateSuccess();
    },
    onError: () => {
      props?.onCreateError && props?.onCreateError();
    },
  });

  return {
    editVendor,
    editVendorError,
    editVendorLoading,
    editVendorSuccess,

    deleteVendor,
    deleteVendorError,
    deleteVendorLoading,
    deleteVendorReset,
    deleteVendorSuccess,
    deleteVendorIsError,

    createVendor,
    createVendorError,
    createVendorLoading,
    createVendorSuccess,
  };
}
